import { QrCode, CreditCard, Wallet, Bitcoin } from 'lucide-react';
import CryptoPaymentOptions from './subscription/CryptoPaymentOptions';
import { useTranslation } from 'react-i18next';
import { analyticsService } from '../services';

// Import PaymentMethod type
type PaymentMethod = 'card' | 'promptpay' | 'qr' | 'crypto';

interface PaymentMethodsProps {
  onSelect: (method: PaymentMethod | string) => void;
  selectedMethod?: string;
  disabledMethods?: PaymentMethod[];
  // Optional props for Purchase Journey tracking
  trackingData?: {
    subscriptionLevel: 'standard' | 'premium';
    subscriptionType: 'monthly' | 'yearly' | 'trial';
    serviceType: 'flower' | 'fruit' | 'cat_grass';
    currency: string;
    price: number;
  };
}

export default function PaymentMethods({ 
  onSelect, 
  selectedMethod,
  disabledMethods = [],
  trackingData
}: PaymentMethodsProps) {
  const { t } = useTranslation();
  
  const paymentMethods = [
    {
      id: 'promptpay' as PaymentMethod,
      name: t('components.paymentMethods.methods.promptpay.name'),
      icon: QrCode,
      description: t('components.paymentMethods.methods.promptpay.description'),
      isPrimary: true
    },
    {
      id: 'card' as PaymentMethod,
      name: t('components.paymentMethods.methods.card.name'),
      icon: CreditCard,
      description: t('components.paymentMethods.methods.card.description')
    },
    {
      id: 'crypto' as PaymentMethod,
      name: t('components.paymentMethods.methods.crypto.name'),
      icon: Bitcoin,
      description: t('components.paymentMethods.methods.crypto.description')
    }
  ];

  return (
    <div className="space-y-3">
      {paymentMethods.map((method) => {
        const isDisabled = disabledMethods.includes(method.id);
        
        return (
          <div
            key={method.id}
            className={`p-4 border rounded-lg transition-all ${
              isDisabled 
                ? 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed' 
                : selectedMethod === method.id
                  ? 'border-[#F9D348] bg-yellow-50 cursor-pointer'
                  : 'border-gray-200 hover:border-gray-300 cursor-pointer'
            }`}
            onClick={() => {
              if (!isDisabled) {
                onSelect(method.id);
                
                // Track add_payment_info event if tracking data is provided
                if (trackingData) {
                  try {
                    const items = [{
                      item_id: `${trackingData.serviceType}_${trackingData.subscriptionLevel}_${trackingData.subscriptionType}`,
                      item_name: `${trackingData.serviceType === 'flower' ? 'Flower' : trackingData.serviceType === 'cat_grass' ? 'Cat Grass' : 'Fruit'} ${trackingData.subscriptionLevel} ${trackingData.subscriptionType} Subscription`,
                      item_category: 'Subscription',
                      item_brand: 'Piika',
                      price: trackingData.price,
                      quantity: 1
                    }];

                    analyticsService.trackAddPaymentInfo(method.id, items, trackingData.currency, trackingData.price);
                    console.log('📊 Tracked add_payment_info for payment method selection:', { paymentMethod: method.id, items });
                  } catch (analyticsError) {
                    console.error('Failed to track add payment info:', analyticsError);
                  }
                }
              }
            }}
          >
            <div className="flex items-center">
              <div className={`p-2 rounded-full mr-3 ${
                isDisabled 
                  ? 'bg-gray-200' 
                  : method.isPrimary 
                    ? 'bg-yellow-100' 
                    : 'bg-gray-100'
              }`}>
                <method.icon size={20} className={
                  isDisabled 
                    ? 'text-gray-400' 
                    : method.isPrimary 
                      ? 'text-yellow-600' 
                      : 'text-gray-600'
                } />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className={`font-medium ${isDisabled ? 'text-gray-400' : ''}`}>
                    {method.name}
                    {method.id === 'crypto' && !isDisabled && (
                      <span className="ml-2 inline-block text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                        {t('components.paymentMethods.methods.crypto.specialOffer')}
                      </span>
                    )}
                  </div>
                  {method.isPrimary && !isDisabled && (
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                      {t('components.paymentMethods.badges.recommended')}
                    </span>
                  )}
                  {isDisabled && (
                    <span className="text-xs bg-gray-200 text-gray-500 px-2 py-1 rounded-full">
                      {t('components.paymentMethods.badges.notAvailable')}
                    </span>
                  )}
                </div>
                <div className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                  {method.description}
                </div>
                {isDisabled && (
                  <div className="text-xs text-gray-400 mt-1 italic">
                    {t('components.paymentMethods.notSupported')}
                  </div>
                )}
              </div>
            </div>
            {method.id === 'crypto' && selectedMethod === 'crypto' && !isDisabled && (
              <div className="text-xs mt-2 p-3 bg-gray-50 rounded-md">
                <div className="mt-2">
                  <span className="font-medium text-gray-700">{t('components.paymentMethods.methods.crypto.supportedNetworks')}</span>
                  <ul className="list-disc pl-5 mt-1 text-gray-600 space-y-1">
                    <li>{t('components.paymentMethods.methods.crypto.networks.arbitrum')}</li>
                    <li>{t('components.paymentMethods.methods.crypto.networks.base')}</li>
                    <li>{t('components.paymentMethods.methods.crypto.networks.polygon')}</li>
                    <li>{t('components.paymentMethods.methods.crypto.networks.bnb')}</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
} 